import { subDays, startOfWeek, startOfMonth, endOfMonth, subMonths, format } from "date-fns"
import type { FilterPreset, FilterPresetPeriodType } from "@/types/filterPreset"
import { ActivityTypeKey } from "@/constants/activity"
import { ActivityType } from "@/types/activity"

const FMT = "yyyy-MM-dd"
const fmt = (d: Date) => format(d, FMT)

/**
 * periodType → 실제 날짜 범위 변환
 */
export function resolvePeriodDates(
  periodType: FilterPresetPeriodType,
  customStart?: string,
  customEnd?: string,
): { dateStart: string; dateFinish: string } {
  const now = new Date()

  switch (periodType) {
    case "last7d":
      return { dateStart: fmt(subDays(now, 7)), dateFinish: fmt(now) }
    case "last14d":
      return { dateStart: fmt(subDays(now, 14)), dateFinish: fmt(now) }
    case "last30d":
      return { dateStart: fmt(subDays(now, 30)), dateFinish: fmt(now) }
    case "thisWeek":
      return { dateStart: fmt(startOfWeek(now, { weekStartsOn: 1 })), dateFinish: fmt(now) }
    case "thisMonth":
      return { dateStart: fmt(startOfMonth(now)), dateFinish: fmt(now) }
    case "lastMonth": {
      const prev = subMonths(now, 1)

      return { dateStart: fmt(startOfMonth(prev)), dateFinish: fmt(endOfMonth(prev)) }
    }
    case "custom":
      return { dateStart: customStart ?? fmt(now), dateFinish: customEnd ?? fmt(now) }
    case "allTime":
      return { dateStart: fmt(new Date(0)), dateFinish: fmt(now) }
    default:
      return { dateStart: fmt(subDays(now, 7)), dateFinish: fmt(now) }
  }
}

/**
 * 프리셋 → Report 페이지 URL 파라미터 변환
 */
export function presetToSearchParams(preset: FilterPreset, activityIds: number[]): URLSearchParams {
  const params = new URLSearchParams()

  if (preset.idfTemplate) {
    params.set("idfTemplate", String(preset.idfTemplate))
  }

  const { dateStart, dateFinish } = resolvePeriodDates(
    preset.periodType,
    preset.customDateStart,
    preset.customDateEnd,
  )
  params.set("dateStart", dateStart)
  params.set("dateFinish", dateFinish)

  if (activityIds.length > 0) {
    params.set("idfActivity", activityIds.join(","))
  }

  if (preset.selectedPlayerIds.length > 0) {
    params.set("idfPlayer", preset.selectedPlayerIds.join(","))
  }

  return params
}

/** 기간 라벨 포매팅 */
export const PERIOD_LABELS: Record<FilterPresetPeriodType, string> = {
  last7d: "Last 7 days",
  last14d: "Last 14 days",
  last30d: "Last 30 days",
  thisWeek: "This week",
  thisMonth: "This month",
  lastMonth: "Last month",
  custom: "Custom",
  allTime: "All time",
}

/** 액티비티 타입 배열 → 표시 문자열 */
export function formatActivityTypes(types: ActivityTypeKey[], t: (key: string) => string): string {
  if (types.length === 0) {
    return t("all")
  }

  return types.map((key) => t(ActivityType[key])).join(", ")
}

/** 선수 수 표시 */
export function formatPlayerCount(ids: number[], totalCount?: number): string {
  if (ids.length === 0 || ids.length === totalCount) {
    return "All players"
  }

  return `${ids.length} players`
}

/**
 * 리포트 URL 생성 (Quick Report / Recent Report 공용)
 * 현재 선택된 팀 ID를 team 파라미터로 포함하여, URL 공유 시 해당 팀으로 진입 가능.
 */
export function generateReportUrl(
  reportType: string,
  params: URLSearchParams,
  idfTeam?: number,
): string {
  const basePath = reportType === "daily" ? "/report/daily" : "/report/trend"
  if (idfTeam) {
    params.set("team", String(idfTeam))
  }

  return `${window.location.origin}${basePath}?${params.toString()}`
}
